import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  View,
  ScrollView,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useProviderProfile } from '@/hooks/use-provider';
import { Input } from '@/components/ui/input';
import { Select, SelectItem } from '@/components/ui/select';
import { ThemedText } from '@/components/themed-text';
import { useProviderEnrollments } from '@/hooks/use-enrollments';
import { useManualCharge } from '@/hooks/use-manual-charge';
import { getApiErrorMessage } from '@/utils/api-error';
import { useToastStore } from '@/store/toastStore';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { parseCurrency } from '@/utils/parse-currency';
import { format, isValid, parse } from 'date-fns';
import { Ionicons } from '@expo/vector-icons';
import { cn } from '@/utils/cn';

// Helper para processar valores de moeda
const handleCurrencyChange = (text: string): string => {
  const digits = text.replace(/\D/g, '');
  if (digits.length === 0) return '';
  const valueInReais = parseInt(digits, 10) / 100;
  return valueInReais.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

type TabKey = 'basic' | 'payment';

type ManualChargeModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enrollmentId?: string;
  enrollmentLabel?: string;
  onSuccess?: () => void;
};

export function ManualChargeModal({
  open,
  onOpenChange,
  enrollmentId,
  enrollmentLabel,
  onSuccess,
}: ManualChargeModalProps) {
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isMobile = width < 768;
  const mobileModalHeight = height - insets.top - insets.bottom - 24;
  const { data: provider } = useProviderProfile();

  const { data: enrollmentsResponse, isLoading: isLoadingEnrollments } = useProviderEnrollments();
  const { addToast } = useToastStore();
  const manualChargeMutation = useManualCharge();

  // Estados do formulário
  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [selectedEnrollment, setSelectedEnrollment] = useState<string | undefined>(enrollmentId);
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [description, setDescription] = useState('');
  const [pixCopyPaste, setPixCopyPaste] = useState('');
  const [paymentLink, setPaymentLink] = useState('');
  const [bankName, setBankName] = useState('');
  const [bankAgency, setBankAgency] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [paymentInstructions, setPaymentInstructions] = useState('');

  // Auto-fill when opening
  useEffect(() => {
    if (
      open &&
      provider &&
      !pixCopyPaste &&
      !paymentLink &&
      !bankAccount &&
      !bankAgency &&
      !bankName
    ) {
      // Em equipe "caixa único", quem recebe é o dono: o perfil vem pronto.
      const pix = provider.paymentProfile?.pixCopyPaste ?? provider.defaultPixCopyPaste;
      const link = provider.paymentProfile?.paymentLink ?? provider.defaultPaymentLink;
      const bank = provider.paymentProfile?.bankDetails ?? provider.defaultBankDetails;
      if (pix) setPixCopyPaste(pix);
      if (link) setPaymentLink(link);
      if (bank) {
        setBankName(bank.bankName || '');
        setBankAgency(bank.bankAgency || '');
        setBankAccount(bank.bankAccount || '');
        setPaymentInstructions(bank.paymentInstructions || '');
      }
    }
  }, [open, provider, pixCopyPaste, paymentLink, bankAccount, bankAgency, bankName]);

  const processingMethod = PROCESSING_METHOD.MANUAL;

  useEffect(() => {
    if (enrollmentId) {
      setSelectedEnrollment(enrollmentId);
    }
  }, [enrollmentId, open]);

  const contentClassName = isMobile
    ? 'w-full overflow-hidden rounded-3xl p-0 shadow-2xl'
    : 'w-[95%] max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-2xl p-0';

  const enrollmentOptions = useMemo(() => {
    const enrollments = enrollmentsResponse?.data;
    if (!enrollments || !Array.isArray(enrollments)) return [];
    return enrollments.map((enrollment) => ({
      label: `${enrollment.service?.name || 'Serviço'} — ${enrollment.client?.user?.username || 'Cliente'}`,
      value: enrollment.id,
    }));
  }, [enrollmentsResponse]);

  const hasNoEnrollments = !enrollmentId && !isLoadingEnrollments && enrollmentOptions.length === 0;

  const resetForm = () => {
    if (!enrollmentId) setSelectedEnrollment(undefined);
    setAmount('');
    setDueDate(format(new Date(), 'dd/MM/yyyy'));
    setDescription('');
    setPixCopyPaste('');
    setPaymentLink('');
    setBankName('');
    setBankAgency('');
    setBankAccount('');
    setPaymentInstructions('');
    setActiveTab('basic');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!selectedEnrollment) {
      addToast('Selecione um contrato para faturar.', 'error');
      setActiveTab('basic');
      return;
    }

    const parsedAmount = parseCurrency(amount);
    if (!amount || parsedAmount == null || parsedAmount <= 0) {
      addToast('Informe um valor válido para a cobrança.', 'error');
      setActiveTab('basic');
      return;
    }

    const parsedDueDate = parse(dueDate, 'dd/MM/yyyy', new Date());
    if (!isValid(parsedDueDate)) {
      addToast('Data de vencimento inválida. Use DD/MM/AAAA.', 'error');
      setActiveTab('basic');
      return;
    }

    if (!pixCopyPaste && !paymentLink && !bankAccount) {
      addToast('Informe pelo menos uma forma de pagamento.', 'error');
      setActiveTab('payment');
      return;
    }

    try {
      await manualChargeMutation.mutateAsync({
        enrollmentId: selectedEnrollment,
        amount: parsedAmount,
        dueDate: format(parsedDueDate, 'yyyy-MM-dd'),
        processingMethod,
        description: description || undefined,
        pixCopyPaste: pixCopyPaste || undefined,
        paymentLink: paymentLink || undefined,
        bankName: bankName || undefined,
        bankAgency: bankAgency || undefined,
        bankAccount: bankAccount || undefined,
        paymentInstructions: paymentInstructions || undefined,
      });
      addToast('Cobrança criada com sucesso.', 'success');
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Não foi possível criar a cobrança.'), 'error');
    }
  };

  const tabs = [
    { key: 'basic' as TabKey, label: 'Dados da Cobrança' },
    { key: 'payment' as TabKey, label: 'Dados para Pagamento' },
  ];

  const modalBodyContent = (
    <>
      <View
        className={cn(
          'bg-neutral-50/90 px-5 pb-4',
          isMobile ? 'pt-5' : 'px-6 pt-6',
        )}
      >
        <View className="flex-row items-start justify-between gap-3">
          <View className="flex-1">
            <ThemedText className={cn('font-bold leading-snug', isMobile ? 'text-lg' : 'text-xl')}>
              Criar cobrança
            </ThemedText>
            <ThemedText className="text-foreground/55 mt-0.5 text-sm leading-5">
              Gere uma cobrança avulsa para um contrato específico.
            </ThemedText>
          </View>
          <Pressable
            onPress={handleClose}
            className="h-9 w-9 items-center justify-center rounded-full bg-neutral-200/80"
            hitSlop={8}
          >
            <Ionicons className="text-neutral-400" name="close" size={18} />
          </Pressable>
        </View>
      </View>

      {/* Tabs */}
      <View className="flex-row border-b border-border px-5">
        {tabs.map((tab) => {
          const isPaymentTab = tab.key === 'payment';
          return (
            <Pressable
              key={tab.key}
              onPress={() => setActiveTab(tab.key)}
              className={`flex-1 py-3 ${
                activeTab === tab.key
                  ? 'border-b-2 border-primary'
                  : 'border-b-2 border-transparent'
              }`}
              testID={isPaymentTab ? 'manual-charge-tab-payment' : undefined}
            >
              <ThemedText
                className={`text-center font-medium text-sm ${
                  activeTab === tab.key ? 'text-primary' : 'text-foreground/60'
                }`}
              >
                {tab.label}
              </ThemedText>
            </Pressable>
          );
        })}
      </View>

      <ScrollView
        style={isMobile ? { flex: 1, minHeight: 0 } : undefined}
        className={isMobile ? undefined : 'max-h-[50vh]'}
        contentContainerClassName="px-5 py-4"
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Tab: Dados Básicos */}
        {activeTab === 'basic' && (
          <View className="mt-4 gap-4 px-1">
            {!enrollmentId && (
              <View className="gap-2">
                <ThemedText className="font-medium text-sm">Contrato / Cliente</ThemedText>
                {isLoadingEnrollments ? (
                  <View className="flex-row items-center gap-2">
                    <ActivityIndicator />
                    <ThemedText className="text-foreground/60 text-sm">
                      Carregando contratos...
                    </ThemedText>
                  </View>
                ) : hasNoEnrollments ? (
                  <View className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                    <ThemedText className="text-sm text-yellow-800">
                      Você ainda não possui contratos ativos.
                    </ThemedText>
                  </View>
                ) : (
                  <Select
                    value={
                      selectedEnrollment
                        ? enrollmentOptions.find((e) => e.value === selectedEnrollment)?.label
                        : ''
                    }
                    onValueChange={setSelectedEnrollment}
                    placeholder="Selecione um contrato"
                    testID="manual-charge-enrollment-select"
                  >
                    {enrollmentOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <ThemedText className="text-sm">{option.label}</ThemedText>
                      </SelectItem>
                    ))}
                  </Select>
                )}
              </View>
            )}

            {enrollmentLabel && (
              <View className="gap-2">
                <ThemedText className="font-medium text-sm">Contrato</ThemedText>
                <View className="bg-muted rounded-lg p-3">
                  <ThemedText className="text-sm">{enrollmentLabel}</ThemedText>
                </View>
              </View>
            )}

            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Valor</ThemedText>
              <Input
                placeholder="R$ 0,00"
                value={amount}
                onChangeText={(text) => setAmount(handleCurrencyChange(text))}
                keyboardType="numeric"
                testID="manual-charge-amount-input"
              />
            </View>

            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Data de vencimento</ThemedText>
              <Input
                placeholder="DD/MM/AAAA"
                value={dueDate}
                onChangeText={setDueDate}
                keyboardType="numbers-and-punctuation"
                testID="manual-charge-due-date-input"
              />
            </View>

            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Descrição / Observações</ThemedText>
              <Input
                placeholder="Ex: Mensalidade referente a Janeiro/2026"
                value={description}
                onChangeText={setDescription}
                multiline
                numberOfLines={2}
                testID="manual-charge-description-input"
              />
            </View>
          </View>
        )}

        {/* Tab: Dados de Pagamento */}
        {activeTab === 'payment' && (
          <View className="mt-4 gap-5 px-1">
            {/* Código PIX */}
            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Código Pix (Copia e Cola)</ThemedText>
              <Input
                placeholder="Cole aqui seu código Pix"
                value={pixCopyPaste}
                onChangeText={setPixCopyPaste}
                multiline
                numberOfLines={3}
                className="min-h-[80px] font-mono text-xs"
                testID="manual-charge-pix-input"
              />
              <ThemedText className="text-foreground/50 text-xs">
                O cliente poderá copiar e pagar diretamente
              </ThemedText>
            </View>

            {/* Link de Pagamento */}
            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Link de Pagamento</ThemedText>
              <Input
                placeholder="https://..."
                value={paymentLink}
                onChangeText={setPaymentLink}
                keyboardType="url"
              />
            </View>

            {/* Dados Bancários */}
            <View className="gap-3 rounded-lg bg-neutral-50 p-4">
              <ThemedText className="font-medium text-sm">Dados Bancários (opcional)</ThemedText>

              <Input
                placeholder="Nome do Banco (ex: Nubank, Itaú)"
                value={bankName}
                onChangeText={setBankName}
              />

              <View className="flex-row gap-2">
                <View className="flex-1">
                  <Input
                    placeholder="Agência"
                    value={bankAgency}
                    onChangeText={setBankAgency}
                    keyboardType="numeric"
                  />
                </View>
                <View className="flex-[2]">
                  <Input placeholder="Conta" value={bankAccount} onChangeText={setBankAccount} />
                </View>
              </View>
            </View>

            {/* Instruções */}
            <View className="gap-2">
              <ThemedText className="font-medium text-sm">Instruções de Pagamento</ThemedText>
              <Input
                placeholder="Instruções adicionais para o cliente..."
                value={paymentInstructions}
                onChangeText={setPaymentInstructions}
                multiline
                numberOfLines={3}
              />
            </View>
          </View>
        )}
      </ScrollView>

      <View className={cn('px-5 pb-4 pt-2', !isMobile && 'px-6')}>
        <DialogFooter className="flex-row justify-end gap-2">
          <Button
            variant="outline"
            onPress={handleClose}
            disabled={manualChargeMutation.isPending}
            className="flex-1 sm:flex-none"
            testID="manual-charge-cancel-btn"
          >
            <ThemedText>Cancelar</ThemedText>
          </Button>
          <Button
            onPress={handleSubmit}
            disabled={
              manualChargeMutation.isPending ||
              (!enrollmentId && (!selectedEnrollment || hasNoEnrollments))
            }
            className="flex-1 sm:flex-none"
            testID="manual-charge-submit-btn"
          >
            {manualChargeMutation.isPending ? (
              <View className="flex-row items-center gap-2">
                <ActivityIndicator size="small" color="white" />
                <ThemedText className="text-white">Enviando...</ThemedText>
              </View>
            ) : (
              <ThemedText className="text-white">Criar cobrança</ThemedText>
            )}
          </Button>
        </DialogFooter>
      </View>
    </>
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => !manualChargeMutation.isPending && onOpenChange(value)}
    >
      <DialogContent
        className={contentClassName}
        style={
          isMobile
            ? {
                height: mobileModalHeight,
                maxHeight: mobileModalHeight,
                width: width - 24,
                flexDirection: 'column',
              }
            : { flexDirection: 'column' }
        }
      >
        <View style={isMobile ? { flex: 1, flexDirection: 'column' } : undefined}>
          {modalBodyContent}
        </View>
      </DialogContent>
    </Dialog>
  );
}
